import { Navigate, type RouteObject } from "react-router";

import { AppShell } from "@/layout/app-shell";
import { PlatformShell } from "@/layout/platform-shell";
import { ConnectMacPage } from "@/pages/connect-mac-page";
import { NotFoundPage, RouteErrorPage } from "@/pages/error-pages";
import { LoginPage } from "@/pages/login-page";
import { NoAccessPage } from "@/pages/no-access-page";
import { SelectOrganizationPage } from "@/pages/select-organization-page";

import {
  HomeRoute,
  RedirectIfSession,
  RequireAccess,
  RequireOrganization,
  RequireSession,
} from "./guards";
import { paths } from "./paths";

/**
 * Cada zona (`src/features/<zona>/routes.tsx`) va en su propio trozo del
 * bundle y se carga al entrar en ella. `zone("certificates", "X")` es la ruta
 * perezosa que pinta el componente `X` de esa zona.
 */
const loaders = {
  home: () => import("@/features/home/routes"),
  certificates: () => import("@/features/certificates/routes"),
  policies: () => import("@/features/policies/routes"),
  people: () => import("@/features/people/routes"),
  activity: () => import("@/features/activity/routes"),
  organization: () => import("@/features/organization/routes"),
  my: () => import("@/features/my/routes"),
  platform: () => import("@/features/platform/routes"),
};

type Loaders = typeof loaders;
type ZoneModule<Z extends keyof Loaders> = Awaited<ReturnType<Loaders[Z]>>;

const zone =
  <Z extends keyof Loaders>(name: Z, component: keyof ZoneModule<Z>) =>
  async () => {
    const module = (await loaders[name]()) as ZoneModule<Z>;
    return {
      Component: module[component] as unknown as React.ComponentType,
    };
  };

/**
 * Árbol de rutas (URLs en inglés; propuesta de UX aprobada). Las guardas son
 * rutas sin `path` que pintan `<Outlet />` o redirigen:
 *
 * - `RequireSession` → sin sesión, a `/login`.
 * - `RequireOrganization` → sin organización elegida, a `/select-organization`.
 * - `RequireAccess` → sin el acceso (`admin`, `platform`), «Sin acceso».
 */
export const appRoutes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: paths.noAccess, element: <NoAccessPage /> },
      {
        element: <RedirectIfSession />,
        children: [{ path: paths.login, element: <LoginPage /> }],
      },
      {
        element: <RequireSession />,
        children: [
          {
            path: paths.selectOrganization,
            element: <SelectOrganizationPage />,
          },
          { path: paths.connectMac, element: <ConnectMacPage /> },

          // Plataforma (Punto): su propio shell, sin organización.
          {
            element: <RequireAccess access="platform" />,
            children: [
              {
                element: <PlatformShell />,
                children: [
                  {
                    path: paths.platform,
                    element: (
                      <Navigate to={paths.platformOrganizations} replace />
                    ),
                  },
                  {
                    path: paths.platformOrganizations,
                    lazy: zone("platform", "PlatformOrganizationsPage"),
                  },
                  {
                    path: paths.platformSupport,
                    lazy: zone("platform", "PlatformSupportPage"),
                  },
                  {
                    path: paths.platformCatalog,
                    lazy: zone("platform", "PlatformCatalogPage"),
                  },
                ],
              },
            ],
          },

          // Organización.
          {
            element: <RequireOrganization />,
            children: [
              {
                element: <AppShell />,
                children: [
                  {
                    element: <HomeRoute />,
                    children: [{ index: true, lazy: zone("home", "HomePage") }],
                  },
                  {
                    path: paths.myCertificates,
                    lazy: zone("my", "MyCertificatesPage"),
                  },
                  {
                    path: paths.myActivity,
                    lazy: zone("my", "MyActivityPage"),
                  },
                  {
                    element: <RequireAccess access="admin" />,
                    children: [
                      {
                        path: paths.certificates,
                        lazy: zone("certificates", "CertificatesListPage"),
                      },
                      {
                        path: paths.certificateNew,
                        lazy: zone("certificates", "CertificateNewPage"),
                      },
                      {
                        path: paths.certificateReplace,
                        lazy: zone("certificates", "CertificateReplacePage"),
                      },
                      {
                        path: paths.certificate,
                        lazy: zone("certificates", "CertificateLayout"),
                        children: [
                          {
                            index: true,
                            element: <Navigate to="overview" replace />,
                          },
                          {
                            path: "overview",
                            lazy: zone(
                              "certificates",
                              "CertificateOverviewTab",
                            ),
                          },
                          {
                            path: "versions",
                            lazy: zone(
                              "certificates",
                              "CertificateVersionsTab",
                            ),
                          },
                          {
                            path: "access",
                            lazy: zone("certificates", "CertificateAccessTab"),
                          },
                          {
                            path: "activity",
                            lazy: zone(
                              "certificates",
                              "CertificateActivityTab",
                            ),
                          },
                        ],
                      },
                      {
                        path: paths.policies,
                        lazy: zone("policies", "PoliciesListPage"),
                      },
                      {
                        path: paths.policyNew,
                        lazy: zone("policies", "PolicyEditorPage"),
                      },
                      {
                        path: paths.policy,
                        lazy: zone("policies", "PolicyEditorPage"),
                      },
                      {
                        lazy: zone("people", "PeopleLayout"),
                        children: [
                          {
                            path: paths.people,
                            lazy: zone("people", "UsersTab"),
                          },
                          {
                            path: paths.groups,
                            lazy: zone("people", "GroupsTab"),
                          },
                        ],
                      },
                      {
                        path: paths.group,
                        lazy: zone("people", "GroupDetailPage"),
                      },
                      {
                        path: paths.person,
                        lazy: zone("people", "UserDetailPage"),
                      },
                      {
                        path: paths.activity,
                        lazy: zone("activity", "ActivityLayout"),
                        children: [
                          {
                            index: true,
                            element: <Navigate to="signatures" replace />,
                          },
                          {
                            path: "signatures",
                            lazy: zone("activity", "SignaturesTab"),
                          },
                          {
                            path: "navigation",
                            lazy: zone("activity", "NavigationTab"),
                          },
                          {
                            path: "admin",
                            lazy: zone("activity", "AdminTab"),
                          },
                        ],
                      },
                      {
                        path: paths.organization,
                        lazy: zone("organization", "OrganizationPage"),
                      },
                    ],
                  },
                  { path: "*", element: <NotFoundPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
